export interface InteractionEvent {
  eventType: 'click' | 'navigation' | 'input' | 'api_outcome';
  target: string;
  path: string;
  metadata?: Record<string, any>;
  outcome?: 'success' | 'failure' | 'neutral';
  timestamp?: string;
  sessionId: string;
}

class InteractionTracker {
  private buffer: InteractionEvent[] = [];
  private batchSize = 10;
  private intervalMs = 10000;
  private sessionId: string;
  private apiUrl: string;

  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.apiUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:4000';
    this.startTimer();

    // Listen to unload to flush
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flush(true));
    }
  }

  private getOrCreateSessionId() {
    let sid = localStorage.getItem('tu_session_id');
    if (!sid) {
      sid = 'sess_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
      localStorage.setItem('tu_session_id', sid);
    }
    return sid;
  }

  public track(event: Omit<InteractionEvent, 'sessionId' | 'timestamp'>) {
    this.buffer.push({
      ...event,
      sessionId: this.sessionId,
      timestamp: new Date().toISOString()
    });

    if (this.buffer.length >= this.batchSize) {
      this.flush();
    }
  }

  private startTimer() {
    setInterval(() => {
      this.flush();
    }, this.intervalMs);
  }

  public async flush(isSync = false) {
    if (this.buffer.length === 0) return;

    const itemsToSend = [...this.buffer];
    this.buffer = [];

    try {
      if (isSync && navigator.sendBeacon) {
        // Use sendBeacon for reliable delivery during unload
        const blob = new Blob([JSON.stringify({ interactions: itemsToSend })], {
          type: 'application/json'
        });
        navigator.sendBeacon(`${this.apiUrl}/api/ai/interactions`, blob);
      } else {
        const token = localStorage.getItem('token');
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`; // Just in case backend enforces it later
        }

        await fetch(`${this.apiUrl}/api/ai/interactions`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ interactions: itemsToSend }),
          keepalive: true
        });
      }
    } catch (e) {
      // Silently fail - tracking shouldn't break the app
      console.warn('Failed to sync interactions', e);
    }
  }
}

export const interactionTracker = new InteractionTracker();
